import { FC, useState } from 'react'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { Spacer, Button } from '@prometeoapi/afrodita-react-component-lib'

import {
  ChangePasswordArgs,
  ChangePasswordFormValidationErrors,
  ChangePasswordReturns,
} from 'src/domain'
import { useUserSettingsUseCases } from 'src/service-locator'
import { ChangePasswordResponseErrors } from 'src/infrastructure'
import {
  mapToChangePasswordFormValidationErrors,
  useMarketplaceFormMutation,
} from 'src/adapters'
import {
  DashboardFormikForm,
  PageHeader,
  PageHeaderTitle,
  useSuccessMutationAlert,
} from 'src/presentation'

import styles from './change-password.module.scss'
import { InputPassword } from 'src/components'
import { InputPasswordIframe } from 'src/components/input-password/input-password-iframe'

export const ChangePasswordPage: FC = () => {
  const { t } = useTranslation(['shared', 'userSettings'])

  const userSettingsUseCases = useUserSettingsUseCases()
  const [oldPasswordSecure, setOldPasswordSecure] = useState('')

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      newPassword: '',
      newPasswordConfirm: '',
    },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .required(
          t('shared:form.errors.requiredField', {
            name: t(
              'userSettings:forms.changePassword.fields.newPassword.label'
            ),
          })
        )
        .min(
          8,
          t('auth:error.length', {
            name: t(`register:registerPage.password`),
          })
        )
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/,
          t('auth:error.regex', {
            name: t(`register:registerPage.password`),
          })
        ),
      newPasswordConfirm: Yup.string()
        .required(
          t('shared:form.errors.requiredField', {
            name: t(
              'userSettings:forms.changePassword.fields.newPasswordConfirm.label'
            ),
          })
        )
        .min(
          8,
          t('auth:error.length', {
            name: t(`register:registerPage.confirmPassword`),
          })
        )
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/,
          t('auth:error.regex', {
            name: t(`register:registerPage.confirmPassword`),
          })
        )
        .oneOf(
          [Yup.ref('newPassword')],
          t('resetpage:resetPasswordConfirm.error.match', {
            name: t(`register:registerPage.confirmPassword`),
          })
        ),
    }),
    onSubmit: (values) => {
      const finalValues = {
        ...values,
        oldPassword: oldPasswordSecure,
      }
      changePassword(finalValues)
    },
  })

  const changePasswordMutation = useMarketplaceFormMutation<
    ChangePasswordArgs,
    ChangePasswordReturns,
    ChangePasswordResponseErrors,
    ChangePasswordFormValidationErrors
  >({
    requestFunction: userSettingsUseCases.changePassword,
    errorMapper: mapToChangePasswordFormValidationErrors,
    onSuccessCallback: () => {
      setShowAlert(true)
      formik.resetForm()
      setOldPasswordSecure('')
    },
    onErrorCallback: (error: any) => {
      const backendErrors = {
        newPassword: error.response?.data?.new_password1,
        newPasswordConfirm: error.response?.data?.new_password2,
      }
      formik.setErrors(backendErrors)
    },
  })

  const {
    mutate: changePassword,
    error: changePasswordError,
    isSuccess: changePasswordIsSuccess,
  } = changePasswordMutation

  const { renderAlert, showAlert, setShowAlert } = useSuccessMutationAlert({
    isSuccess: changePasswordIsSuccess,
    body: t('userSettings:forms.changePassword.successMessage'),
  })

  return (
    <section className={styles['page-container']}>
      <form
        style={{ position: 'absolute', top: '-9999px', left: '-9999px' }}
        aria-hidden="true"
      >
        <input type="text" name="fakeUsername" autoComplete="username" />
        <input
          type="password"
          name="fakePassword"
          autoComplete="current-password"
        />
      </form>
      <PageHeader>
        <PageHeaderTitle>
          {t(`userSettings:header.changePassword.title`)}
        </PageHeaderTitle>
      </PageHeader>

      <Spacer size={'10x'} />

      {showAlert && (
        <>
          {renderAlert()}

          <Spacer size={'10x'} />
        </>
      )}

      <DashboardFormikForm
        formik={formik}
        mutation={changePasswordMutation}
        autoComplete="off"
      >
        <InputPasswordIframe
          name="oldPassword"
          label={t(
            'userSettings:forms.changePassword.fields.oldPassword.label'
          )}
          onChange={setOldPasswordSecure}
          error={
            (!oldPasswordSecure && formik.submitCount > 0
              ? t('shared:form.errors.requiredField', {
                  name: t(
                    'userSettings:forms.changePassword.fields.oldPassword.label'
                  ),
                })
              : changePasswordError?.formFieldErrors?.oldPassword?.[0]) ??
            undefined
          }
        />

        <Spacer />

        <InputPassword
          {...formik.getFieldProps('newPassword')}
          type="password"
          label={t(
            'userSettings:forms.changePassword.fields.newPassword.label'
          )}
          autoComplete="new-password"
          error={
            formik?.errors?.newPassword ||
            changePasswordError?.formFieldErrors?.newPassword
          }
        />

        <Spacer />

        <InputPassword
          {...formik.getFieldProps('newPasswordConfirm')}
          type="password"
          label={t(
            'userSettings:forms.changePassword.fields.newPasswordConfirm.label'
          )}
          autoComplete="new-password"
          error={
            formik.errors.newPasswordConfirm ||
            changePasswordError?.formFieldErrors?.newPasswordConfirm
          }
        />

        <Spacer />

        <Button
          type={'submit'}
          disabled={
            !(
              formik.isValid &&
              formik.values.newPassword &&
              formik.values.newPasswordConfirm &&
              oldPasswordSecure
            )
          }
        >
          {t('userSettings:forms.changePassword.submitButton.label')}
        </Button>
      </DashboardFormikForm>
    </section>
  )
}
